/**
 * Проверяет значение keepUnusedDataFor на соответствие правилам:
 * 1. Значение должно быть безопасным для использования внутри setTimeout.
 *
 * @param keepUnusedDataFor параметр из опций эндпоинта
 */
export function validateKeepUnusedDataFor(keepUnusedDataFor?: number) {
    // Значение таймаута должно укладываться в 32bit signed interger.
    // RTK внутри умножает keepUnusedDataFor на 1000 и передает в setTimeout.
    const maxTimeoutSafeValue = Math.floor(2147483647 / 1000);

    // Не разрешаем выставлять значение меньше нуля, так как это число будет отправлено в setTimeout.
    const minTimeoutSafeValue = 0;

    if (typeof keepUnusedDataFor !== 'number') {
        return;
    }

    if (keepUnusedDataFor < minTimeoutSafeValue) {
        throw new Error(
            `Задано слишком маленькое значение keepUnusedDataFor. Значение должно быть не менее ${minTimeoutSafeValue}.`
        );
    }

    if (keepUnusedDataFor > maxTimeoutSafeValue) {
        throw new Error(
            `Задано слишком большое значение keepUnusedDataFor. Значение должно быть не более ${maxTimeoutSafeValue}.`
        );
    }
}

export interface LinkedList<T> {
    size: () => number;
    head: () => LinkedListItem<T> | null;
    tail: () => LinkedListItem<T> | null;
    append: (value: T) => LinkedListItem<T>;
    prepend: (value: T) => LinkedListItem<T>;
    remove: (item: LinkedListItem<T>) => void;
    setHead: (item: LinkedListItem<T>) => void;
}

interface LinkedListItem<T> {
    prev: LinkedListItem<T> | null;
    next: LinkedListItem<T> | null;
    value: T;
}

export function createLinkedList<T>(): LinkedList<T> {
    let size = 0;
    let head: LinkedListItem<T> | null = null;
    let tail: LinkedListItem<T> | null = null;

    return {
        size: () => size,
        head: () => head,
        tail: () => tail,
        append(value) {
            const item: LinkedListItem<T> = {
                prev: tail,
                next: null,
                value,
            };

            if (tail) {
                tail.next = item;
            }

            tail = item;

            if (!head) {
                head = tail;
            }

            size++;

            return item;
        },
        prepend(value) {
            const item: LinkedListItem<T> = {
                prev: null,
                next: head,
                value,
            };

            if (head) {
                head.prev = item;
            }

            head = item;

            if (!tail) {
                tail = head;
            }

            size++;

            return item;
        },
        remove(item) {
            // У уже удаленного узла и у единственного живого оба указателя равны null,
            // отличить их можно только по голове списка.
            if (item.prev === null && item.next === null && head !== item) {
                return;
            }

            if (item.prev) {
                item.prev.next = item.next;
            } else {
                head = item.next;
            }

            if (item.next) {
                item.next.prev = item.prev;
            } else {
                tail = item.prev;
            }

            item.prev = null;
            item.next = null;

            size--;
        },
        setHead(item) {
            if (item === head) {
                return;
            }

            if (item.prev) {
                item.prev.next = item.next;
            }
            if (item.next) {
                item.next.prev = item.prev;
            } else {
                tail = item.prev;
            }

            item.prev = null;
            item.next = head;

            if (head) {
                head.prev = item;
            }

            head = item;
        },
    };
}
